/**
 * 重要: このファイルはseedスクリプトからインポートされることを想定しています
 * Prismaクライアントや外部依存を持つモジュールをインポートしないでください
 *
 * admin（健保スタッフ向け管理画面）の権限モデルの定義。
 * 設計方針は docs/specs/admin/roles/prd.md、移行手順は同ディレクトリの plan.md を参照。
 */

/**
 * admin の subject（操作対象）ごとに許可されうる action（操作）の定義
 *
 * action は原則 read / write の 2 種に揃える。
 * read / write では表現しきれない操作のみ、専用の action を足す。
 * - send   … 取り消せず外部に届くため、下書きの編集と分ける
 * - export … 一括ダウンロードは漏洩時の影響が段違いなため、一覧の閲覧と分ける
 * - revoke_rejection … 不認定の撤回は適用リーダーに限定する
 * - reject … 不認定はユニットリーダー以上に限定したいため、編集と分ける
 */
export const ADMIN_ABILITY_DEFINITION = {
  // 加入者
  Member: ["read"],
  /** 認証コード。export は認証コード・メールアドレスの CSV 一括ダウンロード */
  MemberAuthCode: ["write", "export"],
  MemberBankAccount: ["read"],

  // 事業所
  Office: ["read", "write"],
  OfficeBankAccount: ["read"],
  /** 加入者のメールアドレスとして許可しないドメイン */
  OfficeEmailDomain: ["read", "write"],
  Employer: ["read", "write"],
  EmployerInvitation: ["read", "write"],

  // 健保スタッフ
  /** スタッフ一覧・削除と、保有ロールの付け替え */
  Staff: ["read", "write"],
  /** スタッフのサインアップ許可リスト（＝スタッフの招待） */
  StaffLoginAllowList: ["read", "write"],

  // アカウント
  Account: ["read", "write"],

  // CSVインポート（誰がどのCSVを取り込むかは種別ごとに決まっているため subject を分ける）
  CsvImportMember: ["read", "write"],
  CsvImportOffice: ["read", "write"],
  CsvImportMemo: ["read", "write"],
  CsvImportElderlyBenefitsCard: ["read", "write"],
  CsvImportOfficeAccount: ["read", "write"],

  // ストレスチェック
  /**
   * ストレスチェックの実施状況。実施回ごとの依頼数・受検数の参照。
   *
   * 実施者（健保の医師・保健師等）が担当する領域で、健診とは担当が別れるため
   * MedicalCheckup とは subject を分ける。
   * 個人結果は実施者以外が閲覧できない建て付けのため、現状は read のみ用意する。
   */
  StressCheck: ["read"],

  // 健診
  MedicalCheckup: ["read", "write"],
  MedicalCheckupResult: ["read", "write"],
  MedicalCheckupReport: ["read", "write"],
  HealthCheckupFacility: ["read", "write"],

  // 申請（CSチームが関わるものと関わらないものがあるため種別ごとに subject を分ける）
  /** 被扶養者異動届。不認定はユニットリーダー限定にしたいため reject を分ける */
  HihuyousyaIdouTodoke: ["read", "write", "reject", "revoke_rejection"],
  Hennyuujikennin: ["read", "write"],
  Hifuyoushashikakukakunin: ["read", "write", "revoke_rejection"],
  Fuufukyoudoufuyoukakunin: ["read", "write", "revoke_rejection"],
  Ryoyohitatekae: ["read", "write"],
  /**
   * 出産手当金。棚卸し（02-screens.csv）とヒアリングシートより後に追加された機能のため
   * どちらにも入っていない。担当は給付チーム（2026-08-21 確認）。
   *
   * 不受理・不支給決定は独立した操作ではなく、ステータスの付け替え
   * （updateShussanteatekinApplicationStatus）の選択肢のひとつなので、
   * 被扶養者異動届のような reject は分けない。分けるなら操作自体の分割が先になる
   */
  Shussanteatekin: ["read", "write"],

  // 通知書
  NotificationPaper: ["read", "write"],

  // 通知・Eメール
  CustomNotification: ["read", "write", "send"],
  SystemNotification: ["read", "write"],
  /** 事業所向けメール通知。現行は EmailDraft の権限を流用しているため専用 subject を切る */
  EmployerEmailNotification: ["read", "write"],
  EmailDraft: ["read", "write"],
  AddressBook: ["read", "write"],
  EmailDelivery: ["read", "write", "send"],
  /** Eメールの送信結果（開封・クリック等）の一覧 */
  EmailActivity: ["read"],
  /** バウンス（宛先不明等で配信できなかったアドレス）の一覧・削除 */
  EmailBounce: ["read", "write"],

  // マスタ・システム
  /** 加入者の認証時のマイナアプリの文字変換ルール */
  CharacterConversionRule: ["read", "write"],
  DeletedRecord: ["read"],
  /**
   * 開発・デバッグ用の画面
   *
   * ログの出力確認・UI カタログ・AI の疎通確認。業務データは扱わない。
   * 個別に subject を切っても運用の判断材料にならないので 1 つにまとめている。
   * `write` は課金や外部への発火を伴う操作（AI 疎通確認の実行）に使う。
   */
  Dev: ["read", "write"],
} as const;

export type AdminSubject = keyof typeof ADMIN_ABILITY_DEFINITION;

/**
 * subject と action の組み合わせ
 *
 * subject ごとに許可された action しか書けないようにするため、subject で分配した union にする
 */
export type AdminAbility = {
  [Sub in AdminSubject]: {
    subject: Sub;
    action: (typeof ADMIN_ABILITY_DEFINITION)[Sub][number];
  };
}[AdminSubject];

/**
 * DBやクライアントから受け取る、まだ検証されていない ability レコード
 *
 * 既知の subject / action かどうかは buildAdminAbility で判定する
 */
export type AdminAbilityRecord = {
  subject: string;
  action: string;
};

/**
 * スタッフに付与できるロール
 *
 * チーム role ＋ ユニットリーダー role ＋ スタッフ管理 role の兼務で運用する。
 * カスタムロールは作らないため、追加・変更はこの定義とマイグレーションで行う。
 *
 * 全権を持つ admin（ボードメンバー）role は作らない。
 * 全権 role を常設すると、現行の admin / user が形骸化した問題をそのまま再生産するため。
 *
 * DB の staff_role enum と対で見るべき定義。
 */
export const ADMIN_ROLES = [
  // チーム role
  "hennyu",
  "kyufu",
  "tekiyo",
  "hoken",
  "cs",
  // ユニットリーダー role（対応するチーム role を包含し、そのチームの特別 action を足したもの）
  "hennyu_leader",
  "kyufu_leader",
  "tekiyo_leader",
  "hoken_leader",
  "cs_leader",
  // システム通知設定の閲覧・編集
  "system_notification_editor",
  // ストレスチェックの実施状況の閲覧
  "stress_check_implementer",
  // Eメールアクティビティの閲覧とバウンスの管理
  "email_manager",
  // スタッフ管理 role（業務 ability は一切持たない）
  "staff_manager",
] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

/**
 * コード側に定義のあるロールか
 *
 * ロール管理 UI から送られてきた値の検証に使う。
 * 定義に無い値は権限としても無視されるが（getAbilitiesForRoles）、
 * DB に入れないよう手前で弾く。
 */
export const isAdminRole = (role: string): role is AdminRole => {
  return (ADMIN_ROLES as ReadonlyArray<string>).includes(role);
};

/**
 * チーム role に割り当てる ability
 *
 * 健保へのヒアリング結果（docs/specs/admin/roles/04-team-role-hearing.csv）から起こしている。
 * 「どのチームがどの画面を使うか」の回答を、各画面が要求する権限
 * （02-screens.csv）経由で subject × action に変換したもの。
 *
 * 申請と CSV インポートは旧権限が種別を区別していないため、画面の種別で解決している。
 * 例: 療養費の画面の editApplication は Ryoyohitatekae:write だけに写す。
 *
 * 不認定（HihuyousyaIdouTodoke:reject）はチーム role には入れない。
 * ユニットリーダー限定の action として ROLE_ABILITIES 側で足す。
 *
 * 機械的な変換で出てこないものが 3 つある。概ね
 * 「呼び出し側が要求する ability を誰も持たない」状態を避けるために足したもの。
 *
 * - EmailDelivery:write … 予約配信のキャンセル（配信詳細画面の操作）。画面自体は
 *   ヒアリング済みだが、旧権限 manageNotification を参照の EmailDelivery:read に
 *   写したため write が導出されない。画面を使う 4 チームに合わせる
 * - SystemNotification:read … システム通知設定の 2 画面。棚卸しより後に追加され
 *   ヒアリング対象になっていなかった。mykenpo 内部の通知内容を見るだけの参照専用画面で、
 *   全チームが見えて問題ないため一律で付与する（2026-08-21 確認）
 * - CsvImportMemo:read … 台帳メモの取り込み履歴と進捗の参照。ほかの CSV 種別は
 *   履歴一覧が listMemberImportHistories などの参照権限に分かれているが、
 *   台帳メモだけ取り込みと同じ権限で守られていたため read が導出されない。
 *   取り込みを行う適用チームに合わせる
 * - Dev:read / Dev:write … 開発・デバッグ用の画面。棚卸しにもヒアリングにも無く、
 *   ログインチェックだけで開けていた（4-e）。業務データを扱わないので
 *   チームで絞る意味が無く、全チームに一律で配る
 * - Staff:read … 健保スタッフとその権限の参照。
 *   ヒアリング項目には入っていたがその時点ではこれがないことの影響が見えていなかったと思われる。
 *   これがないと自分に割り当てられたロールの確認・他の人のロールの確認が不可能で、
 *   運用上の著しい制約になるので全チームに付与。
 *
 * 逆に、機械的な変換では出るが意図的に外しているものが 1 つある。
 * CSV から起こし直すときに戻さないこと。
 *
 * - EmailActivity:read / EmailBounce:read,write … 全チームが「使う」と答えた画面だが、
 *   送達状況の調査とバウンス解除は業務ドメインと直交する作業なので、
 *   専用の email_manager role に切り出している
 */
const TEAM_ROLE_ABILITIES = {
  // 編入。事業所・アカウント・通知/Eメール系を広く持つ
  hennyu: [
    { subject: "Staff", action: "read" },
    { subject: "Account", action: "read" },
    { subject: "Account", action: "write" },
    { subject: "AddressBook", action: "read" },
    { subject: "AddressBook", action: "write" },
    { subject: "CharacterConversionRule", action: "read" },
    { subject: "CharacterConversionRule", action: "write" },
    { subject: "CsvImportMember", action: "read" },
    { subject: "CsvImportMember", action: "write" },
    { subject: "CsvImportOffice", action: "read" },
    { subject: "CsvImportOffice", action: "write" },
    { subject: "CsvImportOfficeAccount", action: "read" },
    { subject: "CsvImportOfficeAccount", action: "write" },
    { subject: "CustomNotification", action: "read" },
    { subject: "CustomNotification", action: "send" },
    { subject: "CustomNotification", action: "write" },
    { subject: "DeletedRecord", action: "read" },
    { subject: "Dev", action: "read" },
    { subject: "Dev", action: "write" },
    { subject: "EmailDelivery", action: "read" },
    { subject: "EmailDelivery", action: "send" },
    { subject: "EmailDelivery", action: "write" },
    { subject: "EmailDraft", action: "read" },
    { subject: "EmailDraft", action: "write" },
    { subject: "Employer", action: "read" },
    { subject: "Employer", action: "write" },
    // read は一覧画面の表示に要る。write は read を含意しないため両方入れる
    // （旧権限 createEmailDraft の写像先が write だけだったので、ヒアリングからの
    // 機械変換では read が出てこない）
    { subject: "EmployerEmailNotification", action: "read" },
    { subject: "EmployerEmailNotification", action: "write" },
    { subject: "EmployerInvitation", action: "read" },
    { subject: "EmployerInvitation", action: "write" },
    { subject: "Fuufukyoudoufuyoukakunin", action: "read" },
    { subject: "Fuufukyoudoufuyoukakunin", action: "write" },
    { subject: "Hifuyoushashikakukakunin", action: "read" },
    { subject: "Hifuyoushashikakukakunin", action: "write" },
    { subject: "HihuyousyaIdouTodoke", action: "read" },
    { subject: "HihuyousyaIdouTodoke", action: "write" },
    { subject: "Hennyuujikennin", action: "read" },
    { subject: "Hennyuujikennin", action: "write" },
    { subject: "Member", action: "read" },
    { subject: "MemberAuthCode", action: "export" },
    { subject: "MemberAuthCode", action: "write" },
    { subject: "MemberBankAccount", action: "read" },
    { subject: "NotificationPaper", action: "read" },
    { subject: "NotificationPaper", action: "write" },
    { subject: "Office", action: "read" },
    { subject: "Office", action: "write" },
    { subject: "OfficeBankAccount", action: "read" },
    { subject: "OfficeEmailDomain", action: "read" },
    { subject: "OfficeEmailDomain", action: "write" },
    { subject: "SystemNotification", action: "read" },
  ],
  // 給付。療養費・出産手当金と支給決定通知書に加え、加入者・事業所の参照と通知/Eメール系
  //
  // 出産手当金はヒアリングシートに無い（棚卸しより後に追加された機能）。
  // 給付チームの担当であることをユーザーに確認して入れている（2026-08-21）
  kyufu: [
    { subject: "Staff", action: "read" },
    { subject: "Account", action: "read" },
    { subject: "Account", action: "write" },
    { subject: "AddressBook", action: "read" },
    { subject: "AddressBook", action: "write" },
    { subject: "CustomNotification", action: "read" },
    { subject: "CustomNotification", action: "send" },
    { subject: "CustomNotification", action: "write" },
    { subject: "Dev", action: "read" },
    { subject: "Dev", action: "write" },
    { subject: "EmailDelivery", action: "read" },
    { subject: "EmailDelivery", action: "send" },
    { subject: "EmailDelivery", action: "write" },
    { subject: "EmailDraft", action: "read" },
    { subject: "EmailDraft", action: "write" },
    { subject: "EmployerEmailNotification", action: "read" },
    { subject: "EmployerEmailNotification", action: "write" },
    { subject: "Member", action: "read" },
    { subject: "MemberAuthCode", action: "export" },
    { subject: "MemberAuthCode", action: "write" },
    { subject: "MemberBankAccount", action: "read" },
    { subject: "NotificationPaper", action: "read" },
    { subject: "NotificationPaper", action: "write" },
    { subject: "Office", action: "read" },
    { subject: "Office", action: "write" },
    { subject: "OfficeEmailDomain", action: "read" },
    { subject: "OfficeEmailDomain", action: "write" },
    { subject: "Ryoyohitatekae", action: "read" },
    { subject: "Ryoyohitatekae", action: "write" },
    { subject: "Shussanteatekin", action: "read" },
    { subject: "Shussanteatekin", action: "write" },
    { subject: "SystemNotification", action: "read" },
  ],
  // 適用。加入者・事業所のマスタ更新と、扶養まわりの申請に加え、
  // アカウント・通知/Eメール系・マスタ管理。
  //
  // 編入・CS の共通部分から、適用が担当しない 4 subject
  // （Employer / EmployerInvitation / OfficeBankAccount / NotificationPaper）を
  // 除いた範囲になる。対応する画面（事業所ユーザー / 事業所ユーザー招待 /
  // 事業所口座一覧 / 通知書管理）はヒアリングで「使わない」と回答されている
  tekiyo: [
    { subject: "Staff", action: "read" },
    { subject: "Account", action: "read" },
    { subject: "Account", action: "write" },
    { subject: "AddressBook", action: "read" },
    { subject: "AddressBook", action: "write" },
    { subject: "CharacterConversionRule", action: "read" },
    { subject: "CharacterConversionRule", action: "write" },
    { subject: "CsvImportElderlyBenefitsCard", action: "read" },
    { subject: "CsvImportElderlyBenefitsCard", action: "write" },
    { subject: "CsvImportMember", action: "read" },
    { subject: "CsvImportMember", action: "write" },
    { subject: "CsvImportMemo", action: "read" },
    { subject: "CsvImportMemo", action: "write" },
    { subject: "CsvImportOffice", action: "read" },
    { subject: "CsvImportOffice", action: "write" },
    { subject: "CustomNotification", action: "read" },
    { subject: "CustomNotification", action: "send" },
    { subject: "CustomNotification", action: "write" },
    { subject: "DeletedRecord", action: "read" },
    { subject: "Dev", action: "read" },
    { subject: "Dev", action: "write" },
    { subject: "EmailDelivery", action: "read" },
    { subject: "EmailDelivery", action: "send" },
    { subject: "EmailDelivery", action: "write" },
    { subject: "EmailDraft", action: "read" },
    { subject: "EmailDraft", action: "write" },
    // read は一覧画面の表示に要る。write は read を含意しないため両方入れる
    // （旧権限 createEmailDraft の写像先が write だけだったので、ヒアリングからの
    // 機械変換では read が出てこない）
    { subject: "EmployerEmailNotification", action: "read" },
    { subject: "EmployerEmailNotification", action: "write" },
    { subject: "Fuufukyoudoufuyoukakunin", action: "read" },
    { subject: "Fuufukyoudoufuyoukakunin", action: "write" },
    { subject: "Hifuyoushashikakukakunin", action: "read" },
    { subject: "Hifuyoushashikakukakunin", action: "write" },
    { subject: "HihuyousyaIdouTodoke", action: "read" },
    { subject: "HihuyousyaIdouTodoke", action: "write" },
    { subject: "Hennyuujikennin", action: "read" },
    { subject: "Hennyuujikennin", action: "write" },
    { subject: "Member", action: "read" },
    { subject: "MemberAuthCode", action: "export" },
    { subject: "MemberAuthCode", action: "write" },
    { subject: "MemberBankAccount", action: "read" },
    { subject: "Office", action: "read" },
    { subject: "Office", action: "write" },
    { subject: "OfficeEmailDomain", action: "read" },
    { subject: "OfficeEmailDomain", action: "write" },
    { subject: "SystemNotification", action: "read" },
  ],
  // 保健事業。健診まわり一式に加え、加入者・事業所の参照と通知/Eメール系
  //
  // アカウントは 2 回目のヒアリングで明示的に「使う」と回答された。
  // 1 回目は回答が無く、健診の年度一覧が listAccounts を流用していたことに由来する
  // 見かけ上の要求だったため入れていなかった。流用そのものは #3227 で解消済み
  hoken: [
    { subject: "Staff", action: "read" },
    { subject: "Account", action: "read" },
    { subject: "Account", action: "write" },
    { subject: "AddressBook", action: "read" },
    { subject: "AddressBook", action: "write" },
    { subject: "CustomNotification", action: "read" },
    { subject: "CustomNotification", action: "send" },
    { subject: "CustomNotification", action: "write" },
    { subject: "Dev", action: "read" },
    { subject: "Dev", action: "write" },
    { subject: "EmailDelivery", action: "read" },
    { subject: "EmailDelivery", action: "send" },
    { subject: "EmailDelivery", action: "write" },
    { subject: "EmailDraft", action: "read" },
    { subject: "EmailDraft", action: "write" },
    { subject: "Employer", action: "read" },
    { subject: "Employer", action: "write" },
    { subject: "EmployerEmailNotification", action: "read" },
    { subject: "EmployerEmailNotification", action: "write" },
    { subject: "EmployerInvitation", action: "read" },
    { subject: "EmployerInvitation", action: "write" },
    { subject: "HealthCheckupFacility", action: "read" },
    { subject: "HealthCheckupFacility", action: "write" },
    { subject: "MedicalCheckup", action: "read" },
    { subject: "MedicalCheckup", action: "write" },
    { subject: "MedicalCheckupReport", action: "read" },
    { subject: "MedicalCheckupReport", action: "write" },
    { subject: "MedicalCheckupResult", action: "read" },
    { subject: "MedicalCheckupResult", action: "write" },
    { subject: "Member", action: "read" },
    { subject: "MemberAuthCode", action: "export" },
    { subject: "MemberAuthCode", action: "write" },
    { subject: "MemberBankAccount", action: "read" },
    { subject: "Office", action: "read" },
    { subject: "Office", action: "write" },
    { subject: "OfficeBankAccount", action: "read" },
    { subject: "OfficeEmailDomain", action: "read" },
    { subject: "OfficeEmailDomain", action: "write" },
    { subject: "SystemNotification", action: "read" },
  ],
  // CS。加入者・事業所からの問い合わせに答えるため参照範囲が広い
  cs: [
    { subject: "Staff", action: "read" },
    { subject: "Account", action: "read" },
    { subject: "Account", action: "write" },
    { subject: "AddressBook", action: "read" },
    { subject: "AddressBook", action: "write" },
    { subject: "CharacterConversionRule", action: "read" },
    { subject: "CharacterConversionRule", action: "write" },
    { subject: "CustomNotification", action: "read" },
    { subject: "CustomNotification", action: "send" },
    { subject: "CustomNotification", action: "write" },
    { subject: "DeletedRecord", action: "read" },
    { subject: "Dev", action: "read" },
    { subject: "Dev", action: "write" },
    { subject: "EmailDelivery", action: "read" },
    { subject: "EmailDelivery", action: "send" },
    { subject: "EmailDelivery", action: "write" },
    { subject: "EmailDraft", action: "read" },
    { subject: "EmailDraft", action: "write" },
    { subject: "Employer", action: "read" },
    { subject: "Employer", action: "write" },
    // read は一覧画面の表示に要る。write は read を含意しないため両方入れる
    // （旧権限 createEmailDraft の写像先が write だけだったので、ヒアリングからの
    // 機械変換では read が出てこない）
    { subject: "EmployerEmailNotification", action: "read" },
    { subject: "EmployerEmailNotification", action: "write" },
    { subject: "EmployerInvitation", action: "read" },
    { subject: "EmployerInvitation", action: "write" },
    { subject: "Fuufukyoudoufuyoukakunin", action: "read" },
    { subject: "Fuufukyoudoufuyoukakunin", action: "write" },
    { subject: "HealthCheckupFacility", action: "read" },
    { subject: "HealthCheckupFacility", action: "write" },
    { subject: "Hifuyoushashikakukakunin", action: "read" },
    { subject: "Hifuyoushashikakukakunin", action: "write" },
    { subject: "HihuyousyaIdouTodoke", action: "read" },
    { subject: "HihuyousyaIdouTodoke", action: "write" },
    { subject: "Hennyuujikennin", action: "read" },
    { subject: "Hennyuujikennin", action: "write" },
    { subject: "MedicalCheckup", action: "read" },
    { subject: "MedicalCheckup", action: "write" },
    { subject: "MedicalCheckupReport", action: "read" },
    { subject: "MedicalCheckupReport", action: "write" },
    { subject: "Member", action: "read" },
    { subject: "MemberAuthCode", action: "export" },
    { subject: "MemberAuthCode", action: "write" },
    { subject: "MemberBankAccount", action: "read" },
    { subject: "NotificationPaper", action: "read" },
    { subject: "NotificationPaper", action: "write" },
    { subject: "Office", action: "read" },
    { subject: "Office", action: "write" },
    { subject: "OfficeBankAccount", action: "read" },
    { subject: "OfficeEmailDomain", action: "read" },
    { subject: "OfficeEmailDomain", action: "write" },
    { subject: "Ryoyohitatekae", action: "read" },
    { subject: "Ryoyohitatekae", action: "write" },
    { subject: "SystemNotification", action: "read" },
  ],
} as const satisfies Record<string, ReadonlyArray<AdminAbility>>;

/**
 * ロールごとの ability
 *
 * スタッフの有効な権限は、保有する全ロールの ability の和集合で決まる（deny は無い）
 */
export const ROLE_ABILITIES: Record<AdminRole, ReadonlyArray<AdminAbility>> = {
  ...TEAM_ROLE_ABILITIES,

  // ユニットリーダーはチーム role を包含する。
  // 付け替え時に「リーダーなのにチーム role を付け忘れて何もできない」事故を防ぐため、
  // チーム role との兼務を前提にせず単体で成立させる。
  hennyu_leader: [...TEAM_ROLE_ABILITIES.hennyu],
  kyufu_leader: [...TEAM_ROLE_ABILITIES.kyufu],
  // 不認定は被扶養者異動届を担当する適用チームのリーダーが持つ。
  // ヒアリングで被扶養者異動届を扱うのは適用・編入・CSと分かったため、給付から移した
  tekiyo_leader: [
    ...TEAM_ROLE_ABILITIES.tekiyo,
    { subject: "HihuyousyaIdouTodoke", action: "reject" },
    { subject: "HihuyousyaIdouTodoke", action: "revoke_rejection" },
    { subject: "Hifuyoushashikakukakunin", action: "revoke_rejection" },
    { subject: "Fuufukyoudoufuyoukakunin", action: "revoke_rejection" },
  ],
  hoken_leader: [...TEAM_ROLE_ABILITIES.hoken],
  cs_leader: [...TEAM_ROLE_ABILITIES.cs],

  // スタッフの招待・編集・ロールの付け替えのみ。業務の ability も特別 action も持たない
  staff_manager: [
    { subject: "Staff", action: "read" },
    { subject: "Staff", action: "write" },
    { subject: "StaffLoginAllowList", action: "read" },
    { subject: "StaffLoginAllowList", action: "write" },
  ],

  // システム通知設定の閲覧・編集のみ。基本的には他のチーム role との併用を想定。
  system_notification_editor: [
    { subject: "SystemNotification", action: "read" },
    { subject: "SystemNotification", action: "write" },
  ],

  // ストレスチェックの実施状況の閲覧のみ。
  // 実施者（健保の医師・保健師等）を想定した role で、どのチーム role にも含めない。
  // 基本的には他のチーム role との併用を想定。
  stress_check_implementer: [{ subject: "StressCheck", action: "read" }],

  // Eメールアクティビティの閲覧とバウンスの管理のみ。
  // 送達状況の調査とバウンス解除は業務ドメインと直交する作業なので、
  // どのチーム role にも含めない。基本的には他のチーム role との併用を想定。
  //
  // アクティビティの閲覧とバウンスの削除を 1 つにまとめているのは、
  // 「バウンスしている宛先を調べて解除する」が一連の作業で、片方だけ持っていても
  // 用を成さないため。
  email_manager: [
    { subject: "EmailActivity", action: "read" },
    { subject: "EmailBounce", action: "read" },
    { subject: "EmailBounce", action: "write" },
  ],
};
